import Splide from "@splidejs/splide";
import { sliderBreakpoints } from "../constant/breakpoint";

const STORAGE_KEY = "recently_viewed_products";
const CARD_SECTION = "recently-viewed-product-card";
const DEFAULT_LIMIT = 16;
const HIDDEN_CLASS = "u-display-none";

type ViewEntry = {
  handle: string;
  timestamp: number;
};

function shopifyRoot() {
  const root =
    (
      window as Window & {
        Shopify?: { routes?: { root?: string } };
      }
    ).Shopify?.routes?.root ?? "/";

  return root.endsWith("/") ? root : `${root}/`;
}

function normalizeHandle(handle: string) {
  if (!handle) return "";

  try {
    return decodeURIComponent(handle.trim());
  } catch {
    return handle.trim();
  }
}

function getStorage() {
  try {
    const storage = localStorage.getItem(STORAGE_KEY);
    if (!storage) return [];

    const data = JSON.parse(storage);
    if (!Array.isArray(data)) return [];

    return data
      .filter((item) => item && typeof item.handle === "string")
      .map((item) => ({
        handle: normalizeHandle(item.handle),
        timestamp: item.timestamp ?? 0,
      }));
  } catch {
    return [];
  }
}

function setStorage(views: ViewEntry[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(views));
  } catch {
    console.error("localStorage unavailable");
  }
}

function currentHandle() {
  const productUrl = document
    .querySelector("product-info[data-url]")
    ?.getAttribute("data-url");
  const fromProductInfo = productUrl?.match(/\/products\/([^/?#]+)/)?.[1];

  if (fromProductInfo) return normalizeHandle(fromProductInfo);

  const fromPath = window.location.pathname.match(/\/products\/([^/?#]+)/)?.[1];

  return fromPath ? normalizeHandle(fromPath) : "";
}

function rememberProduct(handle: string, limit: number) {
  const productHandle = normalizeHandle(handle);
  if (!productHandle) return;

  const views = getStorage().filter((item) => item.handle !== productHandle);
  views.unshift({ handle: productHandle, timestamp: Date.now() });
  setStorage(views.slice(0, limit));
}

function filteredHandles() {
  const current = currentHandle();

  return getStorage()
    .map((item) => item.handle)
    .filter((handle) => handle && handle !== current);
}

function handlesToShow(limit: number) {
  return filteredHandles().slice(0, limit);
}

function parseSlideHtml(html: string) {
  const template = document.createElement("template");
  template.innerHTML = html.trim();

  const slide = template.content.querySelector(".p-recently-viewed__slide");
  if (slide) return slide.outerHTML;

  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.querySelector(".p-recently-viewed__slide")?.outerHTML ?? "";
}

async function fetchSlideHtml(handle: string, sectionId: string) {
  const url = `${shopifyRoot()}products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(sectionId)}`;

  try {
    const response = await fetch(url);
    if (!response.ok) return "";

    return parseSlideHtml(await response.text());
  } catch {
    return "";
  }
}

function getSection(block: HTMLElement) {
  return (
    block.closest(".js-recently-viewed-section") ??
    block.closest(".shopify-section")
  );
}

function isSectionVisible() {
  return handlesToShow(DEFAULT_LIMIT).length > 0;
}

function syncSectionDisplay(block: HTMLElement) {
  const section = getSection(block);
  const visible = isSectionVisible();

  for (const element of [section, block]) {
    if (!element) continue;

    element.classList.toggle(HIDDEN_CLASS, !visible);
  }

  return visible;
}

function mountSplide(block: HTMLElement) {
  const splideRoot = block.querySelector(".splide") as HTMLElement & {
    splide?: Splide;
  };
  if (!splideRoot) return;

  splideRoot.splide?.destroy(true);

  const splide = new Splide(splideRoot, {
    type: "slide",
    mediaQuery: "min",
    perPage: 3,
    perMove: 1,
    gap: "10px",
    arrows: false,
    pagination: false,
    drag: true,
    snap: true,
    trimSpace: false,
    reducedMotion: { speed: 0 },
    padding: { right: "8%" },
    breakpoints: {
      [sliderBreakpoints.md]: {
        perPage: 5,
        gap: "16px",
        arrows: true,
        padding: 0,
      },
      [sliderBreakpoints.lg]: {
        perPage: 7,
      },
      [sliderBreakpoints.xl]: {
        perPage: 8,
        gap: "20px",
      },
    },
  });

  splide.mount();
}

async function renderBlock(block: HTMLElement) {
  const list = block.querySelector("[data-recently-viewed-list]");
  if (!list) return;

  if (!syncSectionDisplay(block)) {
    return;
  }

  const limit = Number(block.dataset.maxProducts || DEFAULT_LIMIT);
  const sectionId = block.dataset.sectionId || CARD_SECTION;
  const handles = handlesToShow(limit);

  const slides = await Promise.all(
    handles.map((handle) => fetchSlideHtml(handle, sectionId)),
  );

  list.innerHTML = slides.filter(Boolean).join("");

  if (list.children.length > 0) {
    mountSplide(block);
  }
}

export function initRecentlyViewedProducts() {
  const handle = currentHandle();
  if (handle) rememberProduct(handle, DEFAULT_LIMIT);

  document
    .querySelectorAll<HTMLElement>(".js-recently-viewed")
    .forEach((block) => {
      renderBlock(block);
    });
}
